import { useEffect, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { ManualSelectionItem } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import {
    selectFilteredCompetitorsDomains,
    selectSelectedCompetitorsDomains,
} from "app/pages/sales-intelligence/modules/company-analysis/store/competitors/selectors";
import { LEADS_ASSIGN_SELECTION_TOP_N_VALUES } from "app/pages/sales-intelligence/sub-modules/company-search/constants/websites-table";
import { LeadsType } from "app/pages/sales-intelligence/sub-modules/company-search/types/common";
import AssignToOtherUsersPanelView from "app/pages/sales-intelligence/sub-modules/assign-to-users/components/AssignToOtherUsersPanel/AssignToOtherUsersPanelView";
import createAssignToUserSelectionDropdown from "app/pages/sales-intelligence/sub-modules/assign-to-users/models/selection-dropdown/factory";
import trackingService from "app/pages/sales-intelligence/sub-modules/assign-to-users/services/tracking/trackingService";
import {
    assignLeadsToUserWithToastThunk,
    fetchAssignableUsersThunk,
} from "app/pages/sales-intelligence/sub-modules/assign-to-users/store/effects";
import {
    selectAssignableUsers,
    selectAssignIsInProgress,
} from "app/pages/sales-intelligence/sub-modules/assign-to-users/store/selectors";
import type { AssignableUser } from "app/pages/sales-intelligence/sub-modules/assign-to-users/types/common";
import type { BaseActivatedPanelProps } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/components/BaseActivatedPanel/types";
import type { SelectionDropdownItem } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/models/selection-dropdown-item/types";
import { useTableSelections } from "app/pages/sales-intelligence/modules/company-analysis/hooks/useTableSelections";

const AssignToUserCompetitivePanel = (props: BaseActivatedPanelProps) => {
    const dispatch = useDispatch();
    const selectedCompetitorsDomains = useSelector(selectSelectedCompetitorsDomains);
    const filteredCompetitorsDomains = useSelector(selectFilteredCompetitorsDomains);
    const isAssignInProgress = useSelector(selectAssignIsInProgress);
    const users = useSelector(selectAssignableUsers);

    const { clearAndCloseRowsSelection, onSelectionChange } = useTableSelections();

    useEffect(() => {
        const ac = new AbortController();

        void dispatch(fetchAssignableUsersThunk({ cancellation: ac.signal }));

        return () => {
            ac.abort();
        };
    }, [dispatch]);

    const handleDeactivate = () => {
        clearAndCloseRowsSelection();
        props.onDeactivate();
    };

    const handleAssignSubmit = async (user: AssignableUser, selectionItem: Readonly<SelectionDropdownItem>) => {
        const domains =
            selectionItem.id === ManualSelectionItem.ID
                ? selectedCompetitorsDomains
                : filteredCompetitorsDomains.slice(0, selectionItem.value);
        if (domains.length === 0) {
            return;
        }

        await dispatch(
            assignLeadsToUserWithToastThunk({
                assigneeId: user.id,
                domains,
            }),
        );
        trackingService.trackUserSelected(domains.length, user.name);
        handleDeactivate();
    };

    const assignToUserSelectionDropdown = useMemo(
        () =>
            createAssignToUserSelectionDropdown(
                {
                    topNValues: LEADS_ASSIGN_SELECTION_TOP_N_VALUES,
                },
                LeadsType.Websites,
            ),
        [],
    );

    return (
        <AssignToOtherUsersPanelView
            {...props}
            selectionDropdown={assignToUserSelectionDropdown}
            isLoading={isAssignInProgress}
            numberOfResults={filteredCompetitorsDomains.length}
            numberOfSelectedItems={selectedCompetitorsDomains.length}
            users={users}
            onAssignSubmit={handleAssignSubmit}
            onSelectionChange={onSelectionChange}
            onDeactivate={handleDeactivate}
        />
    );
};

export default AssignToUserCompetitivePanel;
